import { HttpClient } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { filter, Observable } from 'rxjs';
import { tap } from 'rxjs/operators';
import SafeUrlAssembler from 'safe-url-assembler';
import { AppState } from '../../../app.state';
import { ApiEndpointService } from '../../../core/services/api-endpoint.service';
import { Customer } from '../../models/customer/customer.model';
import { CustomerWebHooks } from '../../models/customer/customer.webhooks.model';
import { CONSTANTS } from '../utility/constants';

@Injectable({ providedIn: 'root' })
export class CustomerService {
    public constructor(private readonly http: HttpClient,
                       private readonly apiEndpointService: ApiEndpointService,
                       private readonly appState: AppState,
    ) {
    }

    public getCustomer(): Observable<Customer> {
        const endpoint = SafeUrlAssembler()
            .template(this.apiEndpointService.getEndpoint('getCustomer'))
            .toString();

        return this.http.get<Customer>(endpoint)
                   .pipe(
                       filter(customer => !!customer),
                       tap((customer) => {
                           this.appState.customer$.set(customer);
                       }));
    }

    public getCustomerLanguage(customer: Customer): string {
        switch (customer?.ShippingStationId?.toString()) {
            case '5':
                return 'de';
            case '6':
                return 'en-gb';
            case '15':
                return 'en';
            case '16':
                return 'fr';
            case '17':
                return 'it';
            case '13':
                return 'zh-hk';
            default:
                return 'en';
        }
    }

    public isUSCustomer(customer: Customer): boolean {
        if (!customer) {
            customer = this.appState.customer$();
        }

        return customer.ShippingStationId.toString() === '15';
    }

    public isGBCustomer(customer: Customer): boolean {
        if (!customer) {
            customer = this.appState.customer$();
        }

        return customer.CustomerId.substring(0, 2).toUpperCase() === 'GB';
    }

    public isDECustomer(customer: Customer): boolean {
        if (!customer) {
            customer = this.appState.customer$();
        }

        return customer.CustomerId.substring(0, 2).toUpperCase() === 'DE';
    }

    public isEUCustomer(customer: Customer): boolean {
        if (!customer) {
            customer = this.appState.customer$();
        }

        return CONSTANTS.EUCountryCodes.includes(customer.CustomerId.substring(0, 2).toUpperCase());
    }

    public isHKCustomer(customer: Customer): boolean {
        if (!customer) {
            customer = this.appState.customer$();
        }

        return customer.CorpContact.Country === 'HK';
    }

    /**
     * Gets a customer webhooks.
     *
     * @returns {Observable<Customer>}
     */
    public getCustomerWebHooks(): Observable<CustomerWebHooks[]> {
        const endpoint = SafeUrlAssembler()
            .template(this.apiEndpointService.getEndpoint('getCustomerWebHooks'))
            .toString();

            return this.http.get<CustomerWebHooks[]>(endpoint);
    }
}
